import * as React from 'react';

import { getLocalTimeZone, today } from '@internationalized/date';
import {
  Weight,
  enumLoadSortOption,
  LoadSortOption,
} from '@rematter/frontend-lib-api-client';
import {
  Button,
  DataGrid,
  Pagination,
  type PaginationProps,
  FloatingToolbar,
  Badge,
  DateValue,
  useMemoDataGridRows,
  ListScreenLayout,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { LucidePlusCircle, LucideTrash2, LucideX } from 'lucide-react';

import { brokeredLoadMarkAsReceivedAtom } from '../../_graphql/mutationBrokeredLoadMarkAsReceived';
import { brokeredLoadMarkAsShippedAtom } from '../../_graphql/mutationBrokeredLoadMarkAsShipped';
import { brokeredLoadMarkAsVoidedAtom } from '../../_graphql/mutationBrokeredLoadMarkAsVoided';
import * as state from '../../_state/brokeredLoadsList.state';
import { useTranslations } from '../../i18n/useTranslations';
import { BrokeredLoadRecord } from '../../types/brokeredLoad';

import { BrokeredLoadCreate } from './BrokeredLoadCreate';
import { BrokeredLoadListFilters } from './BrokeredLoadListFilters';
import { BrokeredLoadMarkAsReceivedDialog } from './dialogs/BrokeredLoadMarkAsReceivedDialog';
import { BrokeredLoadMarkAsShippedDialog } from './dialogs/BrokeredLoadMarkAsShippedDialog';
import { BrokeredLoadMarkAsVoidedDialog } from './dialogs/BrokeredLoadMarkAsVoidedDialog';
import { useBrokeredLoadsListColumns } from './hooks/useBrokeredLoadsListColumns';

import { brokerageRoutePaths } from '~/modules/brokerage/routes';
import { useModuleLocation } from '~/shared/hooks/useModuleLocation';

const COLUMN_TO_SORT_MAP: Record<
  string,
  {
    asc: LoadSortOption;
    desc: LoadSortOption;
  }
> = {
  expectedShipDate: {
    asc: enumLoadSortOption.ExpectedShipDateAsc,
    desc: enumLoadSortOption.ExpectedShipDateDesc,
  },
  shippedDate: {
    asc: enumLoadSortOption.ShippedDateAsc,
    desc: enumLoadSortOption.ShippedDateDesc,
  },
  status: {
    asc: enumLoadSortOption.StatusAsc,
    desc: enumLoadSortOption.StatusDesc,
  },
  createdOn: {
    asc: enumLoadSortOption.CreatedAtAsc,
    desc: enumLoadSortOption.CreatedAtDesc,
  },
};

//
// ⚛️ ⚛️ ⚛️ BROKERED LOADS LIST VIEW ⚛️ ⚛️ ⚛️
//
export const BrokeredLoadsListView = () => {
  const t = useTranslations();
  const [, navigate] = useModuleLocation();

  // Refs
  const clearAllFiltersRef = React.useRef<(() => void) | null>(null);

  // States
  const [isShippedDialogOpen, setIsShippedDialogOpen] = React.useState(false);
  const [isReceivedDialogOpen, setIsReceivedDialogOpen] = React.useState(false);
  const [isVoidDialogOpen, setIsVoidDialogOpen] = React.useState(false);
  const [selectedLoadIdForShipped, setSelectedLoadIdForShipped] =
    React.useState<string | null>(null);
  const [selectedLoadIdForReceived, setSelectedLoadIdForReceived] =
    React.useState<string | null>(null);
  const [selectedLoadForVoid, setSelectedLoadForVoid] =
    React.useState<BrokeredLoadRecord | null>(null);

  // Atoms
  const [selectedKeys, setSelectedKeys] = useAtom(state.$selectedKeys);
  const [offset, setOffset] = useAtom(state.$offset);
  const [limit, setLimit] = useAtom(state.$limit);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useAtom(
    state.$isCreateDialogOpen,
  );
  const hasFiltersActive = useAtomValue(state.$filtersActive);
  const setCurrentSorting = useSetAtom(state.$currentSorting);
  const { data, isFetching, loadingState, error, refetch } = useAtomValue(
    state.$remoteBrokeredLoads,
  );
  const markAsShipped = useSetAtom(brokeredLoadMarkAsShippedAtom({}));
  const markAsReceived = useSetAtom(brokeredLoadMarkAsReceivedAtom({}));
  const markAsVoided = useSetAtom(brokeredLoadMarkAsVoidedAtom({}));

  const toast = useToast();

  const loads = data?.loads ?? [];
  const total = data?.total ?? 0;
  const selectedRows = useMemoDataGridRows(loads, selectedKeys);

  // Callbacks
  const handlePaginationChange = React.useCallback<
    NonNullable<PaginationProps['onChange']>
  >(
    ({ limit: newLimit, offset: newOffset }) => {
      setLimit(newLimit);
      setOffset(newOffset);
    },
    [setLimit, setOffset],
  );
  const handleCreate = React.useCallback(() => {
    setIsCreateDialogOpen(true);
  }, [setIsCreateDialogOpen]);
  const handleLoadClick = React.useCallback(
    (load: BrokeredLoadRecord) => {
      navigate(brokerageRoutePaths.loadDetail, { params: { id: load.id } });
    },
    [navigate],
  );
  const handleVoidLoad = React.useCallback(
    (loadId: string) => {
      const load = loads.find((l) => l.id === loadId);
      if (load) {
        setSelectedLoadForVoid(load);
        setIsVoidDialogOpen(true);
      }
    },
    [loads],
  );
  const handleVoidConfirm = React.useCallback(async () => {
    if (!selectedLoadForVoid) return;
    try {
      await markAsVoided({ loadId: selectedLoadForVoid.id });
      toast.success(
        t.brokerage.loads.messages.voidSuccess(selectedLoadForVoid.loadNumber),
      );
      refetch();
    } catch (error) {
      toast.error(
        `Failed to void load: ${error instanceof Error ? error.message : 'Unknown error'}`,
      );
    } finally {
      setIsVoidDialogOpen(false);
      setSelectedLoadForVoid(null);
    }
  }, [selectedLoadForVoid, markAsVoided, toast, t, refetch]);
  const handleMarkAsShipped = React.useCallback((loadId: string) => {
    setSelectedLoadIdForShipped(loadId);
    setIsShippedDialogOpen(true);
  }, []);
  const handleShipConfirm = React.useCallback(
    async (
      shippedDate: DateValue | undefined,
      lightWeight: Weight | null,
      heavyWeight: Weight | null,
    ) => {
      if (!selectedLoadIdForShipped) return;
      try {
        await markAsShipped({
          loadId: selectedLoadIdForShipped,
          shippedDate: shippedDate ?? today(getLocalTimeZone()),
          lightWeight,
          heavyWeight,
        });
        toast.success(t.brokerage.loads.messages.shippedSuccess);
        refetch();
      } catch (error) {
        toast.error(
          `Failed to mark load as shipped: ${error instanceof Error ? error.message : 'Unknown error'}`,
        );
      } finally {
        setIsShippedDialogOpen(false);
        setSelectedLoadIdForShipped(null);
      }
    },
    [selectedLoadIdForShipped, markAsShipped, toast, t, refetch],
  );
  const handleMarkAsReceived = React.useCallback((loadId: string) => {
    setSelectedLoadIdForReceived(loadId);
    setIsReceivedDialogOpen(true);
  }, []);
  const handleReceiveConfirm = React.useCallback(
    async (receivedDate: DateValue | undefined) => {
      if (!selectedLoadIdForReceived) return;
      try {
        await markAsReceived({
          loadId: selectedLoadIdForReceived,
          receivedDate: receivedDate ?? today(getLocalTimeZone()),
          receivedLineItems: [],
        });
        toast.success(t.brokerage.loads.messages.receivedSuccess);
        refetch();
      } catch (error) {
        toast.error(
          `Failed to mark load as received: ${error instanceof Error ? error.message : 'Unknown error'}`,
        );
      } finally {
        setIsReceivedDialogOpen(false);
        setSelectedLoadIdForReceived(null);
      }
    },
    [selectedLoadIdForReceived, markAsReceived, toast, t, refetch],
  );
  const handleClearAllFilters = React.useCallback(() => {
    clearAllFiltersRef.current?.();
  }, []);
  const handleColumnSort = React.useCallback(
    (columnId: string, direction: 'ascending' | 'descending' | null) => {
      if (direction === null) {
        setCurrentSorting([enumLoadSortOption.CreatedAtDesc]);
      } else {
        const sortMap = COLUMN_TO_SORT_MAP[columnId];
        if (sortMap) {
          const sortOption =
            direction === 'ascending' ? sortMap.asc : sortMap.desc;
          setCurrentSorting([sortOption]);
        }
      }
      setOffset(0);
    },
    [setCurrentSorting, setOffset],
  );

  // Other data hooks
  const { columns, columnVisibilityState } = useBrokeredLoadsListColumns({
    onDoubleClick: handleLoadClick,
    onVoid: handleVoidLoad,
    onMarkAsShipped: handleMarkAsShipped,
    onMarkAsReceived: handleMarkAsReceived,
    onRefetch: refetch,
  });

  const emptyPanelProps = {
    title: t.brokerage.loads.emptyState.title,
    description: t.brokerage.loads.emptyState.subtitle,
    action: (
      <Button
        onPress={handleCreate}
        variant="solid"
        color="primary"
        icon={<LucidePlusCircle />}
      >
        {t.brokerage.loads.toolbar.create}
      </Button>
    ),
    centered: true,
  };

  const emptyResultsPanelProps = {
    title: t.brokerage.loads.emptyResultsState.title,
    description: t.brokerage.loads.emptyResultsState.subtitle,
    action: (
      <Button
        onPress={handleClearAllFilters}
        variant="solid"
        color="primary"
        icon={<LucideX />}
      >
        {t.brokerage.loads.filters.clearAll}
      </Button>
    ),
    centered: true,
  };

  return (
    <div className="@BrokeredLoadsListView flex h-full flex-col">
      <ListScreenLayout
        filters={
          <BrokeredLoadListFilters clearAllFiltersRef={clearAllFiltersRef} />
        }
        actions={
          <Button
            onPress={handleCreate}
            variant="solid"
            color="primary"
            icon={<LucidePlusCircle />}
          >
            {t.brokerage.loads.toolbar.create}
          </Button>
        }
        footer={
          total > limit ? (
            <Pagination
              limit={limit}
              offset={offset}
              total={total}
              onChange={handlePaginationChange}
            />
          ) : undefined
        }
      >
        <DataGrid<BrokeredLoadRecord>
          isLoading={loadingState.isInitialLoading || isFetching}
          columns={columns}
          dataSource={loads}
          rowKey="id"
          aria-label={t.brokerage.loads.table.aria}
          selectionMode="multiple"
          onSelectionChange={setSelectedKeys}
          onColumnSort={handleColumnSort}
          columnVisibilityState={columnVisibilityState}
          className="w-full text-sm"
          fullWidth
          persistColumnOrderKey="brokerage:brokered-loads-list"
          hasError={!!error}
          errorPanelProps={
            error
              ? {
                  description: t.brokerage.loads.content.error(error.message),
                  centered: true,
                }
              : undefined
          }
          emptyPanelProps={
            hasFiltersActive ? emptyResultsPanelProps : emptyPanelProps
          }
        />
      </ListScreenLayout>

      <FloatingToolbar visible={selectedRows.length > 0}>
        <div className="flex flex-row items-center justify-between gap-3">
          <div className="flex flex-row items-center gap-2 text-sm">
            <Badge color="primary" size="small">
              {selectedRows.length}
            </Badge>{' '}
            {t.brokerage.loads.toolbar.selected}
          </div>
          <Button
            variant="outline"
            icon={<LucideTrash2 />}
            isDisabled={selectedRows.length < 1}
            onPress={() => {
              if (selectedRows.length > 0) {
                handleVoidLoad(selectedRows[0].id);
              }
            }}
          >
            {t.brokerage.loads.toolbar.void}
          </Button>
          <LucideX
            className="size-4 cursor-pointer text-neutral/80"
            onClick={() => setSelectedKeys(new Set())}
          />
        </div>
      </FloatingToolbar>

      {isCreateDialogOpen && (
        <BrokeredLoadCreate
          isOpen={isCreateDialogOpen}
          onClose={() => setIsCreateDialogOpen(false)}
          onSuccess={() => {
            setIsCreateDialogOpen(false);
            refetch();
          }}
        />
      )}

      {isVoidDialogOpen && selectedLoadForVoid && (
        <BrokeredLoadMarkAsVoidedDialog
          loadNumber={selectedLoadForVoid.loadNumber}
          onConfirm={handleVoidConfirm}
          onCancel={() => {
            setIsVoidDialogOpen(false);
            setSelectedLoadForVoid(null);
          }}
        />
      )}

      {isShippedDialogOpen && (
        <BrokeredLoadMarkAsShippedDialog
          onMarkAsShipped={handleShipConfirm}
          onCancel={() => setIsShippedDialogOpen(false)}
        />
      )}

      {isReceivedDialogOpen && (
        <BrokeredLoadMarkAsReceivedDialog
          onMarkAsReceived={handleReceiveConfirm}
          onCancel={() => setIsReceivedDialogOpen(false)}
        />
      )}
    </div>
  );
};
